import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Upload, Image, Trash2, Copy, Check, FolderOpen, Search, Grid, List, X } from 'lucide-react'
import { useFileUpload } from '../../hooks/useFirestore'
import { listFiles, deleteFile } from '../../services/firebase'
import { formatFileSize, isImageFile } from '../../utils/helpers'
import { Button, Modal, Input, Card, Badge } from '../../components/common'
import { useEffect } from 'react'

const MediaLibrary = () => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [copiedUrl, setCopiedUrl] = useState(null)

  const { upload, uploading, progress } = useFileUpload()

  // Load files from storage
  const loadFiles = useCallback(async () => {
    setLoading(true)
    try {
      const mediaFiles = await listFiles('uploads')
      setFiles(mediaFiles)
    } catch (error) {
      console.error('Error loading files:', error)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  // Handle file upload
  const handleUpload = async (e) => {
    const uploadedFiles = e.target.files
    if (!uploadedFiles?.length) return

    for (const file of uploadedFiles) {
      const result = await upload(file, `uploads/${Date.now()}_${file.name}`)
      if (result.success) {
        setFiles(prev => [...prev, {
          name: file.name,
          fullPath: `uploads/${Date.now()}_${file.name}`,
          url: result.url,
        }])
      }
    }
  }

  // Handle file delete
  const handleDelete = async () => {
    if (!deleteConfirm) return

    try {
      await deleteFile(deleteConfirm.fullPath)
      setFiles(prev => prev.filter(f => f.fullPath !== deleteConfirm.fullPath))
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Error deleting file:', error)
    }
  }

  // Copy URL to clipboard
  const copyUrl = (url) => {
    navigator.clipboard.writeText(url)
    setCopiedUrl(url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }

  // Filter files by search term
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Media Library</h1>
          <p className="text-gray-500">Upload and manage images and files</p>
        </div>
        <label>
          <Button variant="primary" icon={Upload} as="span">
            {uploading ? `Uploading... ${progress}%` : 'Upload Files'}
          </Button>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleUpload}
            className="hidden"
          />
        </label>
      </div>

      {/* Search & View Controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setView('grid')}
            className={`p-2 ${view === 'grid' ? 'bg-primary-50 text-primary-600' : 'bg-white text-gray-600'}`}
          >
            <Grid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView('list')}
            className={`p-2 ${view === 'list' ? 'bg-primary-50 text-primary-600' : 'bg-white text-gray-600'}`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Upload Drop Zone */}
      <Card animate={false} className="border-2 border-dashed border-gray-300 p-8">
        <label className="flex flex-col items-center cursor-pointer">
          <Upload className="w-12 h-12 text-gray-400 mb-4" />
          <p className="text-gray-600 text-center mb-2">
            <span className="text-primary-600 font-medium">Click to upload</span> or drag and drop
          </p>
          <p className="text-sm text-gray-500">PNG, JPG, GIF, PDF up to 10MB</p>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleUpload}
            className="hidden"
          />
        </label>
      </Card>

      {/* Files Display */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto" />
          <p className="text-gray-500 mt-4">Loading files...</p>
        </div>
      ) : filteredFiles.length > 0 ? (
        view === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredFiles.map((file) => (
              <motion.div
                key={file.fullPath}
                className="group relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer"
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedFile(file)}
              >
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  {isImageFile(file.name) ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FolderOpen className="w-12 h-12 text-gray-400" />
                  )}
                </div>
                <div className="p-2">
                  <p className="text-xs text-gray-600 truncate">{file.name}</p>
                </div>

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      copyUrl(file.url)
                    }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100"
                  >
                    {copiedUrl === file.url ? (
                      <Check className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-700" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteConfirm(file)
                    }}
                    className="p-2 bg-white rounded-lg hover:bg-gray-100"
                  >
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">File</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Type</th>
                  <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredFiles.map((file) => (
                  <tr key={file.fullPath} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {isImageFile(file.name) ? (
                          <img
                            src={file.url}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <FolderOpen className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <span className="font-medium text-dark-100">{file.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="gray" size="sm">
                        {file.name.split('.').pop()?.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyUrl(file.url)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          {copiedUrl === file.url ? (
                            <Check className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Copy className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(file)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
          <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{searchTerm ? 'No files found' : 'No files uploaded yet'}</p>
        </div>
      )}

      {/* File Preview Modal */}
      <Modal
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        title="File Details"
        size="lg"
      >
        {selectedFile && (
          <div className="space-y-4">
            {isImageFile(selectedFile.name) && (
              <img
                src={selectedFile.url}
                alt={selectedFile.name}
                className="w-full max-h-96 object-contain rounded-lg"
              />
            )}
            <div className="space-y-2">
              <p><strong>Name:</strong> {selectedFile.name}</p>
              <p><strong>Path:</strong> {selectedFile.fullPath}</p>
              <div className="flex items-center gap-2">
                <strong>URL:</strong>
                <input
                  type="text"
                  value={selectedFile.url}
                  readOnly
                  className="flex-1 px-3 py-1 bg-gray-100 rounded text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  icon={copiedUrl === selectedFile.url ? Check : Copy}
                  onClick={() => copyUrl(selectedFile.url)}
                >
                  {copiedUrl === selectedFile.url ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete File"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{deleteConfirm?.name}"? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default MediaLibrary
